// The Xone Business plans: what each costs and how many companies it allows. Shared by the website
// (prices and checkout), the API (checkout and reading Stripe) and the web app (the Subscription
// screen). The database keeps its own copy of the limits in plan_company_limit() (migration 0037)
// and is the authority; a test keeps the two in step.

export type PlanId = 'starter' | 'growth' | 'scale';
export type BillingInterval = 'month' | 'year';

export interface Plan {
  id: PlanId;
  name: string;
  /** US dollars per month. A year costs ten months. */
  monthlyUsd: number;
  /** Companies the owner may have; null means no limit. */
  companyLimit: number | null;
}

export const PLANS: readonly Plan[] = [
  { id: 'starter', name: 'Starter', monthlyUsd: 19, companyLimit: 1 },
  { id: 'growth', name: 'Growth', monthlyUsd: 39, companyLimit: 3 },
  { id: 'scale', name: 'Scale', monthlyUsd: 79, companyLimit: null },
];

export const yearlyUsd = (plan: Plan) => plan.monthlyUsd * 10;

/**
 * The currencies the plans are sold in. Each Stripe price (one per lookup key) carries all three as
 * currency options, and checkout names the one to charge.
 */
export const CURRENCIES = ['usd', 'eur', 'zar'] as const;
export type Currency = (typeof CURRENCIES)[number];

export const isCurrency = (value: unknown): value is Currency =>
  typeof value === 'string' && (CURRENCIES as readonly string[]).includes(value);

/** What each plan costs a month in each currency, in whole units. A year costs ten months. */
export const MONTHLY_PRICES: Record<PlanId, Record<Currency, number>> = {
  starter: { usd: 19, eur: 19, zar: 190 },
  growth: { usd: 39, eur: 39, zar: 390 },
  scale: { usd: 79, eur: 79, zar: 790 },
};

// Every European country and territory, the UK, Switzerland and Norway included.
const EUROPE = new Set(
  ('AD AL AT AX BA BE BG BY CH CY CZ DE DK EE ES FI FO FR GB GG GI GR HR HU IE IM IS IT JE LI LT LU LV ' +
    'MC MD ME MK MT NL NO PL PT RO RS SE SI SJ SK SM UA VA XK').split(' '),
);

/** ZAR in South Africa, EUR in Europe, USD everywhere else and when the country is unknown. */
export function currencyForCountry(country: string | null | undefined): Currency {
  const code = (country ?? '').toUpperCase();
  if (code === 'ZA') return 'zar';
  return EUROPE.has(code) ? 'eur' : 'usd';
}

/** Stripe lookup keys, `<plan>_monthly` or `<plan>_yearly`: one active price each. */
export const PRICE_IDS = [
  'starter_monthly',
  'starter_yearly',
  'growth_monthly',
  'growth_yearly',
  'scale_monthly',
  'scale_yearly',
] as const;
export type PriceId = (typeof PRICE_IDS)[number];

export const isPriceId = (value: unknown): value is PriceId =>
  typeof value === 'string' && (PRICE_IDS as readonly string[]).includes(value);

/** The plan and interval a lookup key stands for, or null when it is not one of ours. */
export function parsePriceId(value: unknown): { plan: PlanId; interval: BillingInterval } | null {
  if (!isPriceId(value)) return null;
  const [plan, billing] = value.split('_') as [PlanId, 'monthly' | 'yearly'];
  return { plan, interval: billing === 'yearly' ? 'year' : 'month' };
}

export const planById = (id: string): Plan | undefined => PLANS.find((p) => p.id === id);

/** 60% off the first six monthly payments, for a first subscription only. */
export const INTRO_COUPON = 'intro_60_off_6_months';

/** The intro offer on monthly plans: this much off, for this many monthly payments. */
export const INTRO_PERCENT_OFF = 60;
export const INTRO_MONTHS = 6;

/** What a monthly plan costs during the intro offer, in units with cents (19 → 7.6). */
export const introMonthly = (monthly: number): number => Math.round(monthly * (100 - INTRO_PERCENT_OFF)) / 100;

/** How each plan is described wherever it is sold: the website's /pricing and the app's plan picker. */
export interface PlanDetails {
  /** The line under the plan's name. */
  scope: string;
  /** The plan the pricing shows as most popular. */
  popular: boolean;
  features: readonly string[];
}

export const PLAN_DETAILS: Record<PlanId, PlanDetails> = {
  starter: {
    scope: '1 company, unlimited users',
    popular: false,
    features: ['Unlimited users', 'Invoicing & quotes', 'Bank feeds & reconciliation', 'Expense tracking', 'Core reports'],
  },
  growth: {
    scope: 'Up to 3 companies, unlimited users',
    popular: true,
    features: ['Everything in Starter', '3 companies, no extra cost', 'Bills', 'Multi-currency'],
  },
  scale: {
    scope: 'Unlimited companies and users',
    popular: false,
    features: ['Everything in Growth', 'Unlimited companies', 'Consolidated reporting', 'Priority support'],
  },
};

const SYMBOL: Record<Currency, string> = { usd: '$', eur: '€', zar: 'R' };

/** "$19", "R7,900", or with `cents` "€7.60" (but "R76" for a whole amount). */
export function formatPrice(amount: number, currency: Currency, cents = false): string {
  const digits = cents && Math.round(amount * 100) % 100 !== 0 ? 2 : 0;
  return SYMBOL[currency] + amount.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** What a plan costs a month in a currency, in whole units. */
export const monthlyPrice = (plan: PlanId, currency: Currency): number => MONTHLY_PRICES[plan][currency];
